import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, tap } from 'rxjs';
import { EnquiryHeader, EnquiryHeaderItem } from 'src/app/shared/models/data/enquiry.model';
import { ExtraCost, ExtraCostParametersValues, ItemExtraCosts } from 'src/app/shared/models/data/extraCostParametersValues.model';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftToasterService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-toaster.service';
import { SelectOption } from 'src/app/shared/teiasoft-bucket/teiasoft-select/teiasoft-select.component';

@Component({
  selector: 'app-modal-extra-costs',
  templateUrl: './modal-extra-costs.component.html',
  styleUrls: ['./modal-extra-costs.component.scss']
})
export class ModalExtraCostsComponent implements OnInit, AfterViewInit {
  @Input() isMobile: boolean = false;
  @Input() theme: string = "silver";
  @Input() enquiryNo!: string;
  @Input() enquiryHeader?: EnquiryHeader;
  @Input() item!: EnquiryHeaderItem;
  @Input() readOnly: boolean = false;

  isInitialLoad: boolean = true;
  isLoading?: boolean;
  extraCostParametersValues?: ExtraCostParametersValues;
  itemExtraCosts!: ItemExtraCosts;
  extraCostsOptionsList!: SelectOption[]; // List of extra cost options for the dropdown
  availableExtraCostsList: SelectOption[] = [];
  extraCostOptionsByRow: SelectOption[][] = [];
  uomOptionsList: { [key: number]: SelectOption[] } = {}; // UOM options tied to each extra cost

  @Output() onSave = new EventEmitter<ExtraCost[]>();
  @Output() onCancel = new EventEmitter<void>();

  constructor(private backendService: BackendService, private teiasoftToaster: TeiasoftToasterService, private translate: TranslateService) { }

  ngOnInit(): void {
    this.isLoading = true;
    // Wait for both API calls to complete
    forkJoin({
      extraCosts: this.getExtraCostsParameters(),
      itemExtraCosts: this.getItemExtraCosts()
    }).subscribe({
      complete: () => {
        if (this.itemExtraCosts.itemExtraCosts?.length == 0)
          this.addExtraCost(); // add a default empty extra cost ready to insert

        this.updateAvailableExtraCosts();
        this.rebuildExtraCostOptionsByRow();
        this.isLoading = false;
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 500); // this was added because the ts-select components emits changes on first load even if not changes have been applied
  }


  onCancelClick() {
    this.onCancel.emit();
  }

  onSaveClick() {
    if (this.validateExtraCosts()) {
      this.onSave.emit(this.itemExtraCosts.itemExtraCosts);
    }
  }

  addNewEmptyExtraCost() {
    if (!this.itemExtraCosts) {
      this.itemExtraCosts = {
        itemExtraCosts: [{
          extraCostId: undefined,
          extraCostAmount: 0,
          extraCostUom: ''
        }]
      }
    }

  }

  validateExtraCosts(): boolean {
    // Track extra cost IDs to check for duplicates
    const extraCostIds = new Set<number>();

    for (let i = 0; i < this.itemExtraCosts.itemExtraCosts!.length; i++) {
      const cost = this.itemExtraCosts.itemExtraCosts![i];

      // Check if any of the inputs are empty or undefined
      if (!cost.extraCostId || (!cost.extraCostAmount || cost.extraCostAmount == 0) || !cost.extraCostUom) {
        this.teiasoftToaster.show("error", this.translate.instant('errors.fieldMandatory'));
        return false;
      }

      if (this.isPerTestUom(cost.extraCostUom) && !Number.isFinite(cost.numberOfTests)) {
        this.teiasoftToaster.show("error", this.translate.instant('errors.fieldMandatory'));
        return false;
      }

      // Check for duplicated extraCostId
      if (extraCostIds.has(cost.extraCostId!)) {
        this.teiasoftToaster.show("error", this.translate.instant('errors.extraCostDuplicated'));
        return false;
      }
      extraCostIds.add(cost.extraCostId!);
    }

    return true; // All inputs are valid
  }

  isPerTestUom(uom?: string | null): boolean {
    return (uom ?? '').toString().trim().toLowerCase().endsWith('/test');
  }

  getExtraCostsParameters(): Observable<any> {
    return this.backendService.getExtraCostsParametersValues(this.enquiryHeader?.currency).pipe(
      tap(res => {
        this.extraCostParametersValues = res;

        if (this.extraCostParametersValues && this.extraCostParametersValues.extraCosts!.length > 0) {

          let validExtraCost;
          if (this.item.unitSystem! == 'Metric') {
            validExtraCost = res.extraCosts!.filter(ec => ec.extraCostMetricUoms && ec.extraCostMetricUoms.length > 0); // && !ec.isOrderExtraCost
          } else {
            validExtraCost = res.extraCosts!.filter(ec => ec.extraCostImperialUoms && ec.extraCostImperialUoms.length > 0); //  && !ec.isOrderExtraCost
          }

          // Prepare the extra cost dropdown options
          this.extraCostsOptionsList = validExtraCost.map(ex => ({
            value: ex.extraCostId,
            text: ex.extraCostName,
            disabled: ex.isOrderExtraCost
          }));

          // Prepare the UOM options for each extra cost
          this.extraCostParametersValues.extraCosts!.forEach(ex => {
            if (this.item.unitSystem! == 'Metric') {
              this.uomOptionsList[ex.extraCostId!] = ex.extraCostMetricUoms!.map(uom => ({
                value: uom,
                text: uom
              }));
            } else {
              this.uomOptionsList[ex.extraCostId!] = ex.extraCostImperialUoms!.map(uom => ({
                value: uom,
                text: uom
              }));
            }
          });
        }
      })
    );
  }

  getExtraCostsOptionsList(currentExtraCostId: number | undefined) {
    // return all extra cost options except for the current selcted and already used in other rows
    const appliedExtraCostsIds = this.itemExtraCosts.itemExtraCosts?.map(ex => ex.extraCostId);

    return this.extraCostsOptionsList.filter(opt => !appliedExtraCostsIds?.includes(opt.value) || opt.value == currentExtraCostId);
  }

  getItemExtraCosts(): Observable<any> {
    return this.backendService.getItemExtraCosts(this.enquiryNo, this.item.itemNo!).pipe(
      tap(res => {
        this.itemExtraCosts = res;
      })
    );
  }

  onExtraCostChange(extraCostId: number, index: number) {
    if (this.isInitialLoad)
      return;

    const selectedUoms = this.uomOptionsList[extraCostId] || [];
    this.itemExtraCosts.itemExtraCosts![index].extraCostId = extraCostId;
    this.itemExtraCosts.itemExtraCosts![index].extraCostUom = selectedUoms[0]?.value || '';

    this.updateAvailableExtraCosts();
    this.rebuildExtraCostOptionsByRow();
  }

  onInputAmount(evt: any, index: any) {
    const amount = evt;
    this.itemExtraCosts.itemExtraCosts![index].extraCostAmount = amount;
  }

  onInputTestAmount(evt: any, index: any) {
    const amount = evt;
    this.itemExtraCosts.itemExtraCosts![index].numberOfTests = Number(amount);
  }

  onExtraCostUomChange(uom: string, index: number) {
    this.itemExtraCosts.itemExtraCosts![index].extraCostUom = uom;
  }

  addExtraCost() {
    // add transport as default  if missing
    const transport = this.extraCostParametersValues!.extraCosts!.find(e => e.extraCostName == 'Transport Extra Cost');

    if (transport && this.itemExtraCosts.itemExtraCosts?.findIndex(ec => ec.extraCostId == transport.extraCostId) == -1) {
      const defaultUom = this.uomOptionsList[transport.extraCostId!]?.[0]?.value || '';
      this.itemExtraCosts.itemExtraCosts?.push({
        extraCostId: transport.extraCostId,
        extraCostName: transport.extraCostName,
        extraCostAmount: 0,
        extraCostUom: defaultUom
      });
    }
    else {
      this.itemExtraCosts.itemExtraCosts?.push({
        extraCostId: undefined,
        extraCostAmount: 0,
        extraCostUom: ''
      });
    }

    this.updateAvailableExtraCosts();
    this.rebuildExtraCostOptionsByRow();
  }

  removeExtraCost(index: number) {
    this.itemExtraCosts.itemExtraCosts?.splice(index, 1);
    this.updateAvailableExtraCosts();
    this.rebuildExtraCostOptionsByRow();
  }

  updateAvailableExtraCosts() {
    if (!this.itemExtraCosts?.itemExtraCosts || !this.extraCostsOptionsList) {
      this.availableExtraCostsList = [];
      return;
    }

    const usedValuesInOtherRows = this.itemExtraCosts?.itemExtraCosts!
      .map(r => r.extraCostId);

    this.availableExtraCostsList = this.extraCostsOptionsList.filter(opt => {
      const isUsedByOtherRow = usedValuesInOtherRows.includes(opt.value);
      return !isUsedByOtherRow;
    });
  }

  private rebuildExtraCostOptionsByRow(): void {
    if (!this.itemExtraCosts?.itemExtraCosts || !this.extraCostsOptionsList) {
      this.extraCostOptionsByRow = [];
      return;
    }

    this.extraCostOptionsByRow = this.itemExtraCosts.itemExtraCosts.map(row =>
      this.getExtraCostsOptionsList(row.extraCostId)
    );
  }

  isOrderExtraCost(extraCost: ExtraCost): boolean {
    const ec = this.extraCostParametersValues?.extraCosts?.find(ex => ex.extraCostId == extraCost.extraCostId);
    return !!(ec && ec.isOrderExtraCost);
  }
}
